import { Fragment, useMemo, useState } from "react";
import { ChevronRight, ImageOff } from "lucide-react";
import type { ProductStat } from "@/services/wholesale-analytics";
import { cn } from "@/lib/utils";
import { fmtKm } from "@/lib/wholesale-format";
import { filterProducts, nextSort, sortProducts, type ProductSortKey, type SortState } from "@/lib/wholesale-analytics-table";
import { Section, THEAD_ROW, Delta, SellThroughBar, SortTh, TableSearch, ShowAll, fmtInt } from "./parts";

const TOP = 20;

function Thumb({ src }: { src: string }) {
  return src ? (
    <img src={src} alt="" loading="lazy" className="hidden sm:block h-8 w-8 shrink-0 object-cover bg-muted" />
  ) : (
    <span className="hidden sm:grid h-8 w-8 shrink-0 bg-muted place-items-center"><ImageOff className="h-3.5 w-3.5 text-muted-foreground" /></span>
  );
}

const fmtStock = (s: number | null) => (s === null ? "—" : fmtInt(Math.max(0, s)));

export function ProductsSection({ products, compare }: { products: ProductStat[]; compare: boolean }) {
  const [sort, setSort] = useState<SortState<ProductSortKey>>({ key: "units", dir: "desc" });
  const [query, setQuery] = useState("");
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const sorted = useMemo(() => sortProducts(filterProducts(products, query), sort), [products, query, sort]);
  // Pretraga već sužava listu, pa se pogoci ne skraćuju na TOP.
  const showAll = all || query.trim() !== "";
  const rows = showAll ? sorted : sorted.slice(0, TOP);
  const onSort = (k: ProductSortKey) => setSort((s) => nextSort(s, k));

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  return (
    <Section
      title="Artikli"
      hint={<>Sell-through = VP prodano ÷ (VP prodano + današnje stanje). Stanje je današnje i dijeli se s web shopovima, pa je za stariji period samo orijentacija. Klik na naslov kolone sortira.</>}
      action={products.length > 0 && <TableSearch value={query} onChange={setQuery} placeholder="Traži artikal ili šifru" />}
    >
      {products.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">Nijedan artikal nije prodan u ovom periodu.</p>
      ) : sorted.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">Nijedan artikal ne odgovara pretrazi „{query.trim()}".</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={THEAD_ROW}>
                  <SortTh k="name" sort={sort} onSort={onSort} align="left" className="pl-3">Artikal</SortTh>
                  <SortTh k="units" sort={sort} onSort={onSort}>Kom.</SortTh>
                  {compare && <SortTh k="delta" sort={sort} onSort={onSort} className="hidden sm:table-cell">Promjena</SortTh>}
                  <SortTh k="revenue" sort={sort} onSort={onSort} className="hidden sm:table-cell">Promet</SortTh>
                  <SortTh k="customers" sort={sort} onSort={onSort} className="hidden md:table-cell">Kupci</SortTh>
                  <SortTh k="stock" sort={sort} onSort={onSort} className="hidden sm:table-cell">Stanje</SortTh>
                  <SortTh k="str" sort={sort} onSort={onSort} className="pr-3">Sell-through</SortTh>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const expandable = p.variations.length > 0;
                  const isOpen = open.has(p.key);
                  return (
                    <Fragment key={p.key}>
                      <tr className={cn("border-b hover:bg-muted/30 transition-colors", isOpen && "bg-muted/30")}>
                        <td className="py-2 px-2 pl-3">
                          <div className="flex items-center gap-2 min-w-0">
                            {expandable ? (
                              <button
                                type="button"
                                onClick={() => toggle(p.key)}
                                aria-expanded={isOpen}
                                aria-label={isOpen ? `Sakrij varijacije: ${p.name}` : `Prikaži varijacije: ${p.name}`}
                                className="p-0.5 -ml-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              >
                                <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-90")} />
                              </button>
                            ) : (
                              <span className="w-4 shrink-0" />
                            )}
                            <Thumb src={p.imageUrl} />
                            <div className="min-w-0">
                              <div className="font-medium truncate max-w-[9rem] sm:max-w-[18rem]" title={p.name}>{p.name}</div>
                              <div className="text-[10px] text-muted-foreground truncate">
                                {p.sku || "bez šifre"}
                                {expandable && ` · ${p.variations.length} var.`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums font-medium">{fmtInt(p.units)}</td>
                        {compare && (
                          <td className="py-2 px-2 text-right hidden sm:table-cell"><Delta current={p.units} previous={p.prevUnits} /></td>
                        )}
                        <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap hidden sm:table-cell">{fmtKm(p.revenue)}</td>
                        <td className="py-2 px-2 text-right tabular-nums hidden md:table-cell">{p.customers}</td>
                        <td className="py-2 px-2 text-right tabular-nums hidden sm:table-cell text-muted-foreground">{fmtStock(p.stock)}</td>
                        <td className="py-2 px-2 pr-3 text-right"><SellThroughBar value={p.str} units={p.units} stock={p.stock} /></td>
                      </tr>
                      {isOpen && p.variations.map((v) => (
                        <tr key={`${p.key}:${v.wcVariationId}`} className="border-b bg-muted/30 text-muted-foreground">
                          <td className="py-1.5 px-2 pl-9 sm:pl-[4.5rem]">
                            <span className="text-foreground">{v.label}</span>
                            {v.sku && <span className="ml-2 text-[10px]">{v.sku}</span>}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-foreground">{fmtInt(v.units)}</td>
                          {compare && <td className="py-1.5 px-2 hidden sm:table-cell" />}
                          <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap hidden sm:table-cell">{fmtKm(v.revenue)}</td>
                          <td className="py-1.5 px-2 hidden md:table-cell" />
                          <td className="py-1.5 px-2 text-right tabular-nums hidden sm:table-cell">{fmtStock(v.stock)}</td>
                          <td className="py-1.5 px-2 pr-3 text-right text-foreground"><SellThroughBar value={v.str} units={v.units} stock={v.stock} /></td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!query.trim() && sorted.length > TOP && <ShowAll open={all} total={sorted.length} onToggle={() => setAll(!all)} />}
        </>
      )}
    </Section>
  );
}
